/**
 * ════════════════════════════════════════════════════════════════════════════════
 * Budget Analytics Functions
 * Extracted from budgetService.js for better modularity
 * Read-only analytics and aggregation functions for budget data
 * ════════════════════════════════════════════════════════════════════════════════
 */

import { db } from '@/config/firebase';
import { 
  collection, 
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { findCategoryById, getCategoryColor } from '@/constants/unifiedCategories';

/**
 * FINLIST CONTEXT: Maps legacy shopping template sub-categories to canonical category IDs
 * This ensures old transactions aggregate correctly under their parent expense categories.
 */
const LEGACY_CATEGORY_MAP = {
  // Weekly Groceries template sub-categories
  'Fruits & Vegetables': 'groceries-food',
  'Dairy & Eggs': 'groceries-food',
  'Meat & Protein': 'groceries-food',
  'Meat & Seafood': 'groceries-food',
  'Bakery': 'groceries-food',
  'Pantry': 'groceries-food',
  'Pantry Staples': 'groceries-food',
  'Beverages': 'groceries-food',
  'Snacks & Sweets': 'groceries-food',
  'Frozen Foods': 'groceries-food',
  // Party Supplies template sub-categories
  'Decorations': 'groceries-food',
  'Food': 'groceries-food',
  'Drinks': 'groceries-food',
  'Tableware': 'groceries-food',
  'Party Favors': 'groceries-food',
  // Meal Prep template sub-categories
  'Proteins': 'groceries-food',
  'Vegetables': 'groceries-food',
  'Grains': 'groceries-food',
  'Meal Prep Containers': 'groceries-food',
  // Household template sub-categories
  'Cleaning': 'groceries-food',
  'Paper Products': 'groceries-food',
  'Batteries & Bulbs': 'groceries-food',
  'Household Items': 'groceries-food',
  // Personal Care
  'Personal Care': 'personal-care',
  'Toiletries': 'personal-care',
  // Legacy category names
  'Groceries': 'groceries-food',
  'Groceries & Food': 'groceries-food',
  'groceries-food': 'groceries-food',
  'personal-care': 'personal-care',
  'Other': 'other'
};

/**
 * Normalize a category name/id to its canonical category ID
 * @param {string} categoryNameOrId - Legacy category name or ID
 * @returns {string} - Canonical category ID
 */
function normalizeToCanonicalCategoryId(categoryNameOrId) {
  if (!categoryNameOrId) return 'other';
  
  // Check direct mapping first
  if (LEGACY_CATEGORY_MAP[categoryNameOrId]) {
    return LEGACY_CATEGORY_MAP[categoryNameOrId];
  }
  
  // Check if it's already a canonical ID by trying to find it
  const category = findCategoryById(categoryNameOrId);
  if (category) {
    return categoryNameOrId;
  }
  
  // Fallback to 'other'
  return 'other';
}

/**
 * @typedef {Object} TopCategory
 * @property {string} categoryId - Category identifier
 * @property {string} categoryName - Display name
 * @property {number} amount - Total amount
 * @property {number} percentage - Percentage of total
 */

/**
 * @typedef {Object} GoalsSummary
 * @property {number} totalGoalContributions - Total contributed to goals this period
 * @property {Object|null} primaryGoal - Most active or nearest-due goal
 */

/**
 * Get top income sources for the budget period
 * @param {string} userId - User ID
 * @param {Date} startDate - Period start
 * @param {Date} endDate - Period end
 * @param {number} limit - Max results (default 3)
 * @returns {Promise<TopCategory[]>}
 */
export async function getTopIncomeSources(userId, startDate, endDate, limit = 3) {
  try {
    if (!userId) throw new Error('User ID required');

    // Query all transactions for the period (without type filter to handle legacy data)
    const transactionsRef = collection(db, 'users', userId, 'transactions');
    const q = query(transactionsRef);

    const snapshot = await getDocs(q);
    
    // FINLIST FIX: Calculate income lookback start date
    // For income, look back to the earlier of: startDate OR start of current calendar month
    // This ensures income added "today" is always counted even if budget starts later
    const now = new Date();
    const startOfCurrentMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const incomeStartDate = startDate < startOfCurrentMonth ? startDate : startOfCurrentMonth;
    
    // Helper to convert date to JS Date
    const toJSDate = (dateVal) => {
      if (!dateVal) return null;
      if (dateVal.toDate) return dateVal.toDate();
      if (typeof dateVal === 'string') return new Date(dateVal);
      if (dateVal instanceof Date) return dateVal;
      return null;
    };
    
    // Filter client-side to handle legacy data without type/archived fields
    const filteredDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      
      // Check if archived (default to false for legacy data)
      if (data.archived === true) return false;
      
      // Check if income - check both type and categoryType for compatibility
      const txType = data.type || data.categoryType;
      if (txType !== 'income') return false;
      
      // Check date range - use extended lookback for income
      const txDate = toJSDate(data.date);
      if (!txDate) return false;
      return txDate >= incomeStartDate && txDate <= endDate;
    });
    
    if (import.meta.env.DEV) {
      console.log('[BUDGET SERVICE] getTopIncomeSources query:', {
        userId,
        startDate: startDate.toISOString(),
        incomeStartDate: incomeStartDate.toISOString(),
        endDate: endDate.toISOString(),
        totalDocs: snapshot.docs.length,
        filteredCount: filteredDocs.length
      });
    }
    
    // Group by category/source
    const sourceMap = {};
    let totalIncome = 0;

    filteredDocs.forEach(doc => {
      const data = doc.data();
      // Use Math.abs for consistency across all transaction aggregation
      const amount = Math.abs(Number(data.amount) || 0);
      const source = data.categoryId || data.source || data.category || 'other';
      
      // Look up category name from unified categories
      const categoryInfo = findCategoryById(source);
      const sourceName = categoryInfo?.name || data.description || source;
      
      // Get transaction date for display
      const txDate = toJSDate(data.date);

      totalIncome += amount;
      if (!sourceMap[source]) {
        sourceMap[source] = { 
          categoryId: source, 
          categoryName: sourceName, 
          amount: 0,
          color: getCategoryColor(source), // Include unified category color
          latestDate: txDate, // Track most recent transaction date
          transactions: [] // Track individual transactions with dates
        };
      }
      sourceMap[source].amount += amount;
      
      // Track individual transactions with their dates and amounts
      sourceMap[source].transactions.push({
        date: txDate,
        amount: amount,
        description: data.description || ''
      });
      
      // Update latest date if this transaction is more recent
      if (txDate && (!sourceMap[source].latestDate || txDate > sourceMap[source].latestDate)) {
        sourceMap[source].latestDate = txDate;
      }
    });

    // Convert to array, calculate percentages, sort, and limit
    const sources = Object.values(sourceMap)
      .map(s => ({
        ...s,
        percentage: totalIncome > 0 ? Math.round((s.amount / totalIncome) * 100) : 0
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, limit);

    return sources;
  } catch (error) {
    console.error('[BUDGET SERVICE] Error getting top income sources:', error);
    return [];
  }
}

/**
 * Get top spending categories for the budget period
 * @param {string} userId - User ID
 * @param {Date} startDate - Period start
 * @param {Date} endDate - Period end
 * @param {number} limit - Max results (default 3)
 * @returns {Promise<TopCategory[]>}
 */
export async function getTopSpendingCategories(userId, startDate, endDate, limit = 3) {
  try {
    if (!userId) throw new Error('User ID required');

    // Query all transactions for the period first, then filter client-side
    // This handles both old data (no type/archived fields) and new data
    const transactionsRef = collection(db, 'users', userId, 'transactions');
    
    // Try to get transactions - handle both date formats
    let snapshot;
    try {
      // First try with Timestamp (new format)
      const q = query(
        transactionsRef,
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate))
      );
      snapshot = await getDocs(q);
    } catch (e) {
      // Fallback: get all transactions and filter client-side
      console.warn('[BUDGET SERVICE] Date query failed, fetching all transactions:', e.message);
      snapshot = await getDocs(transactionsRef);
    }
    
    if (import.meta.env.DEV) {
      console.log('[BUDGET SERVICE] getTopSpendingCategories query:', {
        userId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        totalDocs: snapshot.docs.length
      });
    }
    
    // Group by category - filter for expenses
    const categoryMap = {};
    let totalExpenses = 0;
    let matchedCount = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      
      // FINLIST SSOT: Include expenses AND refunds (legacy type: 'refund')
      const docType = data.type || data.categoryType || 'expense';
      if (docType !== 'expense' && docType !== 'refund') return;
      
      // Filter: must not be archived (or no archived field = assume not archived)
      if (data.archived === true) return;
      
      // Filter: check date range (handle both string and Timestamp dates)
      let docDate;
      if (data.date?.toDate) {
        docDate = data.date.toDate();
      } else if (typeof data.date === 'string') {
        docDate = new Date(data.date);
      } else {
        return; // Skip if no valid date
      }
      
      if (docDate < startDate || docDate > endDate) return;
      
      matchedCount++;
      // FINLIST SSOT: Handle refunds with negative amounts OR legacy type: 'refund'
      const rawAmount = Number(data.amount) || 0;
      const isRefundTx = data.status === 'Refunded' || data.isRefund === true || rawAmount < 0 || docType === 'refund';
      const amount = Math.abs(rawAmount);
      
      // FINLIST CONTEXT: Normalize category to canonical ID
      // Priority: categoryId > normalized category name > 'other'
      const rawCatId = data.categoryId || data.category || 'other';
      const catId = normalizeToCanonicalCategoryId(rawCatId);
      
      // Look up category name from unified categories using canonical ID
      const categoryInfo = findCategoryById(catId);
      const catName = categoryInfo?.name || catId;

      // Debug: Log category resolution
      if (import.meta.env.DEV && rawCatId !== catId) {
        console.log('[BUDGET ANALYTICS] Category normalized:', {
          rawCatId,
          normalizedCatId: catId,
          resolvedName: catName
        });
      }

      // FINLIST SSOT: Refunds reduce totalExpenses
      if (isRefundTx) {
        totalExpenses -= amount;
      } else {
        totalExpenses += amount;
      }
      if (!categoryMap[catId]) {
        categoryMap[catId] = { 
          categoryId: catId, 
          categoryName: catName, 
          amount: 0,
          refunds: 0,
          color: getCategoryColor(catId) // Include unified category color
        };
      }
      // FINLIST SSOT: Track refunds separately per category
      if (isRefundTx) {
        categoryMap[catId].refunds += amount;
        categoryMap[catId].amount -= amount; // Net amount
      } else {
        categoryMap[catId].amount += amount;
      }
    });

    if (import.meta.env.DEV) {
      console.log('[BUDGET SERVICE] getTopSpendingCategories filtered:', {
        matchedCount,
        totalExpenses,
        categories: Object.keys(categoryMap).length
      });
    }

    // Convert to array, calculate percentages, sort, and limit
    const categories = Object.values(categoryMap)
      .map(c => ({
        ...c,
        percentage: totalExpenses > 0 ? Math.round((c.amount / totalExpenses) * 100) : 0
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, limit);

    return categories;
  } catch (error) {
    console.error('[BUDGET SERVICE] Error getting top spending categories:', error);
    return [];
  }
}

/**
 * Get goals and savings summary for the budget period
 * @param {string} userId - User ID
 * @param {Date} startDate - Period start
 * @param {Date} endDate - Period end
 * @returns {Promise<GoalsSummary>}
 */
export async function getGoalsSummary(userId, startDate, endDate) {
  try {
    if (!userId) throw new Error('User ID required');

    // Helper to convert date to JS Date
    const toJSDate = (dateVal) => {
      if (!dateVal) return null;
      if (dateVal.toDate) return dateVal.toDate();
      if (typeof dateVal === 'string') return new Date(dateVal);
      if (dateVal instanceof Date) return dateVal;
      return null;
    };

    // Get all transactions and filter client-side for savings
    const transactionsRef = collection(db, 'users', userId, 'transactions');
    const txSnapshot = await getDocs(transactionsRef);
    
    // Filter for savings transactions in date range
    const savingsDocs = txSnapshot.docs.filter(doc => {
      const data = doc.data();
      if (data.type !== 'savings') return false;
      
      const txDate = toJSDate(data.date);
      if (!txDate) return false;
      return txDate >= startDate && txDate <= endDate;
    });
    
    // Calculate total contributions and track contributions per goal
    let totalGoalContributions = 0;
    const goalContributions = {};

    savingsDocs.forEach(doc => {
      const data = doc.data();
      const amount = Number(data.amount) || 0;
      const goalId = data.goalId;
      
      // Only count positive contributions (not withdrawals)
      if (amount > 0 && !data.meta?.isWithdrawal) {
        totalGoalContributions += amount;
        if (goalId) {
          goalContributions[goalId] = (goalContributions[goalId] || 0) + amount;
        }
      }
    });

    // Get savings goals to find primary goal
    const goalsRef = collection(db, 'users', userId, 'savingsGoals');
    const goalsSnapshot = await getDocs(goalsRef);
    
    let primaryGoal = null;
    let highestContribution = -1; // Changed to -1 so goals with 0 contributions still show
    let mostRecentGoal = null;
    let mostRecentDate = null;

    goalsSnapshot.docs.forEach(doc => {
      const goal = { id: doc.id, ...doc.data() };
      const contributed = goalContributions[doc.id] || 0;
      
      const target = goal.targetAmount || goal.target || 0;
      const current = goal.currentAmount || goal.currentSaved || goal.current || 0;
      const progress = target > 0 ? Math.round((current / target) * 100) : 0;
      
      // Estimate finish date based on contribution rate
      let expectedFinishDate = null;
      if (contributed > 0 && current < target) {
        const remaining = target - current;
        const monthsToFinish = remaining / contributed;
        const finishDate = new Date();
        finishDate.setMonth(finishDate.getMonth() + Math.ceil(monthsToFinish));
        expectedFinishDate = finishDate.toISOString().split('T')[0];
      } else if (current >= target && target > 0) {
        expectedFinishDate = 'Complete';
      } else if (goal.targetDate) {
        // Use the goal's target date if available
        expectedFinishDate = goal.targetDate;
      }

      const goalInfo = {
        id: doc.id,
        name: goal.name || goal.goalName || 'Unnamed Goal',
        progress: Math.min(100, progress),
        expectedFinishDate,
        contributedThisPeriod: contributed
      };
      
      // Track goal with most contributions this period
      if (contributed > highestContribution) {
        highestContribution = contributed;
        primaryGoal = goalInfo;
      }
      
      // Also track most recently created goal as fallback
      const createdAt = goal.createdAt?.toDate?.() || goal.createdAt;
      if (createdAt && (!mostRecentDate || createdAt > mostRecentDate)) {
        mostRecentDate = createdAt;
        mostRecentGoal = goalInfo;
      }
    });

    // If no goal had contributions, show most recently created active goal
    if (!primaryGoal && mostRecentGoal) {
      primaryGoal = mostRecentGoal;
    }
    
    // If still no primary goal but goals exist, just pick the first one
    if (!primaryGoal && goalsSnapshot.docs.length > 0) {
      const firstGoal = goalsSnapshot.docs[0];
      const goal = firstGoal.data();
      const target = goal.targetAmount || goal.target || 0;
      const current = goal.currentAmount || goal.currentSaved || goal.current || 0;
      const progress = target > 0 ? Math.round((current / target) * 100) : 0;
      
      primaryGoal = {
        id: firstGoal.id,
        name: goal.name || goal.goalName || 'Unnamed Goal',
        progress: Math.min(100, progress),
        expectedFinishDate: goal.targetDate || null,
        contributedThisPeriod: 0
      };
    }

    // Build ALL goals list for display (sorted by progress descending)
    const allGoals = goalsSnapshot.docs.map(doc => {
      const goal = { id: doc.id, ...doc.data() };
      const target = goal.targetAmount || goal.target || 0;
      const current = goal.currentAmount || goal.currentSaved || goal.current || 0;
      const progress = target > 0 ? Math.round((current / target) * 100) : 0;
      const contributed = goalContributions[doc.id] || 0;
      
      // Estimate finish date based on contribution rate
      let expectedFinishDate = null;
      if (contributed > 0 && current < target) {
        const remaining = target - current;
        const monthsToFinish = remaining / contributed;
        const finishDate = new Date();
        finishDate.setMonth(finishDate.getMonth() + Math.ceil(monthsToFinish));
        expectedFinishDate = finishDate.toISOString().split('T')[0];
      } else if (current >= target && target > 0) {
        expectedFinishDate = 'Complete';
      } else if (goal.targetDate) {
        expectedFinishDate = goal.targetDate;
      }
      
      return {
        id: doc.id,
        name: goal.name || goal.goalName || 'Unnamed Goal',
        progress: Math.min(100, progress),
        expectedFinishDate,
        contributedThisPeriod: contributed,
        targetAmount: target,
        currentAmount: current,
        category: goal.category || null,
        status: goal.status || 'active'
      };
    })
    // Filter to only active goals (not completed/archived)
    .filter(g => g.status === 'active' || g.progress < 100)
    // Sort by progress (highest first) then by contribution this period
    .sort((a, b) => {
      if (b.progress !== a.progress) return b.progress - a.progress;
      return b.contributedThisPeriod - a.contributedThisPeriod;
    });

    return {
      totalGoalContributions,
      primaryGoal,
      allGoals // NEW: All goals for display
    };
  } catch (error) {
    console.error('[BUDGET SERVICE] Error getting goals summary:', error);
    return {
      totalGoalContributions: 0,
      primaryGoal: null
    };
  }
}
